import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { actionTypes } from "./actions";

/** How many recently opened projects the sidebar remembers. */
export const RECENT_PROJECTS_LIMIT = 5;

const initialCommonState = {
  onboarding: false,
  customerOnBoarding: false,
  userOnBoarding: false,
  templateOnBoarding: false,
  factorOnBoarding: false,
  projectOnBoarding: false,
  singleProjectOnBoarding: false,
  roleOnBoarding: false,
  teamOnBoarding: false,
  tagOnBoarding: false,

  templateBuilderOnBoarding: false,
  reportsOnBoarding: false,
  kanbanOnBoarding: false,
  timeTrackingOnBoarding: false,
  ticketsOnBoarding: false,
  chatOnBoarding: false,
  organizationOnBoarding: false,
  treeOnBoarding: false,
  ganttOnBoarding: false,
  scheduledOnBoarding: false,
  sprintsOnBoarding: false,
  projectModalOnBoarding: false,
  viewsOnBoarding: false,

  tourReplay: null,
  /* Bumped on every press of the header's replay button so pressing it twice in
   * a row re-fires the tour instead of looking dead. */
  tourReplayNonce: 0,

  dismissedHints: {},

  /* uuid -> last_reply_at the user has already read on that ticket */
  ticketsSeen: {},

  /* organization uuid -> the one-time welcome has been shown */
  welcomedOrgs: {},

  /* uuid of the project whose board should still show the "hand this to someone"
   * prompt the setup wizard promised. Cleared the moment it is dismissed, so it
   * appears once, on one board. */
  boardAssignHint: null,

  /* module key -> "cards" | "table". Absent means the module's own default. */
  listView: {},

  /* organization uuid -> [{ uuid, title, image }] the user pinned to the
   * sidebar, newest first. Persisted with the rest of this slice, so it is per
   * browser rather than per account. */
  projectPins: {},

  /* organization uuid -> the same shape for the projects last opened. */
  projectRecents: {},


  quickAccess: {},

  /* «به سازمان فلان وارد شدی» — گذاشته می‌شود درست پیش از این‌که تعویض سازمان
   * صفحه را دوباره بار کند، و بعد از بالا آمدن، لایه‌ی اصلی آن را نشان می‌دهد و
   * پاک می‌کند. یک شیء `{ title, at }`؛ `at` برای این‌که یک اعلانِ جامانده،
   * روزها بعد و بی‌ربط، دوباره ظاهر نشود. */
  orgSwitchNotice: null,

  /* `{ title, avatar, at }` of a switch that is under way, or null. Persisted
   * on purpose — it is what lets the splash come back up on the far side of the
   * reload — and cleared by the splash itself once it has faded out. */
  orgSwitchPending: null,

  /* Set by `BasePage` when this document has finished loading its organization.
   * Blacklisted below, so every reload starts at false: a persisted value would
   * answer "was an organization ever loaded", which is not the question. */
  orgBootDone: false,

  subModal: false,
  notifRequested: null,
  notifPromptCount: 0,
  /* When the user was first seen on an authorised screen. The notification
   * prompt waits five minutes from this stamp, so a reload doesn't restart the
   * clock. */
  notifFirstSeenAt: null,
  tempData: null,
  theme: "light",
};

export const reducer = persistReducer(
  {
    storage,
    key: "taskamoon-user-common",
    blacklist: ["tourReplay", "tourReplayNonce", "orgBootDone"],
  },
  (state = initialCommonState, action) => {
    switch (action.type) {
      /* `tourReplay` used to clear itself as soon as the first tour on the page
       * marked itself as seen. On screens with panel tours that killed the
       * replay before the queued panel tour got its turn, so the guide only
       * reappeared after navigating away and back. It now lives until the route
       * changes — see the layout, which clears it on navigation. */
      case actionTypes.SET_CUSTOM_ONBOARDING:
        return { ...state, ...action.payload };

      case actionTypes.SEE_TICKET:
        return {
          ...state,
          ticketsSeen: {
            ...state.ticketsSeen,
            [action.payload.uuid]: action.payload.lastReplyAt,
          },
        };

      case actionTypes.SEE_ORG_WELCOME:
        return {
          ...state,
          welcomedOrgs: {
            ...state.welcomedOrgs,
            [action.payload]: true,
          },
        };

      case actionTypes.TOGGLE_PROJECT_PIN: {
        const { orgUuid, project } = action.payload;
        const current = state.projectPins?.[orgUuid] || [];
        const exists = current.some((item) => item.uuid === project.uuid);

        return {
          ...state,
          projectPins: {
            ...state.projectPins,
            [orgUuid]: exists
              ? current.filter((item) => item.uuid !== project.uuid)
              : [project, ...current],
          },
        };
      }

      case actionTypes.VISIT_PROJECT: {
        const { orgUuid, project } = action.payload;
        const current = state.projectRecents?.[orgUuid] || [];

        return {
          ...state,
          projectRecents: {
            ...state.projectRecents,
            /* Re-inserted at the front on every visit, and capped — past a
             * handful it stops being "where I was" and becomes another flat
             * list. Storing the project again also refreshes a renamed title. */
            [orgUuid]: [
              project,
              ...current.filter((item) => item.uuid !== project.uuid),
            ].slice(0, RECENT_PROJECTS_LIMIT),
          },
        };
      }

      case actionTypes.SET_LIST_VIEW:
        return {
          ...state,
          listView: {
            ...state.listView,
            [action.payload.module]: action.payload.view,
          },
        };

      case actionTypes.DISMISS_HINT:
        return {
          ...state,
          dismissedHints: {
            ...state.dismissedHints,
            [action.payload]: true,
          },
        };

      case actionTypes.SET_QUICK_ACCESS:
        return {
          ...state,
          quickAccess: {
            ...state.quickAccess,
            [action.payload.scopeKey]: action.payload.state,
          },
        };

      case actionTypes.SET_SUB_MODAL:
        return {
          ...state,
          subModal: action.payload,
        };

      case actionTypes.SET_VARIABLES:
        return {
          ...state,
          ...action.payload,
        };

      case actionTypes.SET_TEMPDATA:
        return {
          ...state,
          ...action.payload,
        };

      default:
        return state;
    }
  }
);
